// src/components/PermissionManager.js

import React, { useState } from 'react';
import { useAccessControl } from '../contexts/AccessControlContext';
import { useAuth } from '../contexts/AuthContext';
import FeatureGuard from './FeatureGuard';
import GuideAccessManager from './GuideAccessManager';

const PermissionManager = () => {
  const { permissions, updateFieldPermissions, FIELD_ACCESS_LEVELS } = useAccessControl();
  const { userRole } = useAuth();
  const [saving, setSaving] = useState({});
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('field-permissions');

  const handlePermissionChange = async (fieldName, role, newAccess) => {
    const key = `${fieldName}-${role}`;
    setSaving({ ...saving, [key]: true });
    
    try {
      const success = await updateFieldPermissions(fieldName, role, newAccess);
      if (success) {
        setMessage(`✅ Updated ${fieldName} access for ${role} to ${newAccess.replace('_', ' ')}`);
      } else {
        setMessage(`❌ Failed to update ${fieldName} for ${role}`);
      }
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    }
    
    setSaving({ ...saving, [key]: false });
    
    // Clear message after 3 seconds
    setTimeout(() => setMessage(''), 3000);
  };

  const getAccessColor = (access) => {
    switch(access) {
      case 'hidden': return { bg: '#f8f9fa', color: '#6c757d', border: '#dee2e6' };
      case 'view_only': return { bg: '#e3f2fd', color: '#1976d2', border: '#bbdefb' };
      case 'edit': return { bg: '#e8f5e8', color: '#388e3c', border: '#c8e6c9' };
      case 'full': return { bg: '#f3e5f5', color: '#7b1fa2', border: '#e1bee7' };
      default: return { bg: '#f8f9fa', color: '#6c757d', border: '#dee2e6' };
    }
  };

  const roles = ['viewer', 'editor', 'admin', 'superadmin'];
  
  const [expandedGroups, setExpandedGroups] = useState({
    'Guide General Settings': {
      expanded: false,
      subGroups: {
        'Core Information': false,
        'Itinerary Details': false,
        'Location Coordinates': false,
        'Classification': false,
        'Translation Settings': false
      }
    },
    'Guide Stop': {
      expanded: false,
      subGroups: {
        'Meta': false,
    'Core Identification': false,
    'Media': false,
        'Location': false,
        'Core Content': false,
        'Audio Narration': false,
        'Metadata': false,
        'Supplementary Content': false,
        'Navigation': false
      }
    },
    'System Features': {
      expanded: false,
      subGroups: {
        'Application Features': false,
        'Content Management': false,
        'Export Features': false,
        'RAG Management': false,
        'Action Buttons': false,
        'Navigation Tabs': false,
        'Analytics': false
      }
    }
  });

  const toggleParentGroup = (parentGroup) => {
    setExpandedGroups(prev => ({
      ...prev,
      [parentGroup]: {
        ...prev[parentGroup],
        expanded: !prev[parentGroup].expanded
      }
    }));
  };

  const toggleSubGroup = (parentGroup, groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [parentGroup]: {
        ...prev[parentGroup],
        subGroups: {
          ...prev[parentGroup].subGroups,
          [groupName]: !prev[parentGroup].subGroups[groupName]
        }
      }
    }));
  };

  const fieldGroups = {

    'Guide General Settings': {
      'Core Information': ['isIntroductoryStop', 'ai_generated_personalization', 'thumbnailUrl', 'theme', 'itinerary_name', 'country', 'city', 'filename'],
      'Itinerary Details': ['itinerary_duration', 'itinerary_difficulty', 'itinerary_rating', 'itinerary_description'],
      'Location Coordinates': ['itinerary_coordinates', 'guide_type'],
      'Classification': ['itinerary_categories', 'itinerary_tags'],
      'Translation Settings': ['translated_itinerary', 'parent_guide_id']
    },
    'Guide Stop': {
      'Meta': ['ai_settings'],
      'Core Identification': ['name', 'subtitle', 'category', 'tier'],
      'Media': ['photoUrl', 'sphericalPhotoUrl'],
      'Location': ['coordinates'],
      'Core Content': ['inDepthDescription', 'description'],
      'Audio Narration': ['audioUrl'],
      'Metadata': ['estimatedDuration', 'openingHours', 'keywords'],
      'Supplementary Content': ['historicalContext', 'culturalSignificance', 'hiddenGems'],
      'Navigation': ['nextStop']
    },
    'System Features': {
      'Application Features': ['userManagement', 'permissionConfig', 'tourPlanning', 'translation', 'audioGeneration','regeneratePointIds','addNewPoi','SaveLoad' ],
      'Export Features': ['firebaseExport', 'sqlExport', 'urlGeneration', 'saveDraft', 'saveJSON'],
      'RAG Management': ['ragManagement'],
      'Action Buttons': ['generateSQL', 'runSQL', 'export', 'loadGuide'],
      'Navigation Tabs': ['aiTools', 'drafts', 'metadata', 'pois', 'preview', 'map'],
      'Analytics': ['analytics', 'analyticsOverview', 'analyticsDaily', 'analyticsQR', 'analyticsAudio', 'analyticsGeo', 'analyticsSessions']
    }
  };

  const fieldDisplayNames = {
    thumbnailUrl: 'Thumbnail Image',
    isIntroductoryStop: 'Introduction by Sofia',
    ai_generated_personalization: 'AI Generated Personalization',
    itinerary_name: 'Itinerary Name',
    itinerary_duration: 'Duration (days)',
    itinerary_difficulty: 'Difficulty',
    itinerary_rating: 'Rating',
    itinerary_description: 'Description',
    itinerary_coordinates: 'Location Coordinates',
    itinerary_categories: 'Categories',
    itinerary_tags: 'Tags',
    guide_type: 'Guide Type',
    translated_itinerary: 'Translated Itinerary',
    parent_guide_id: 'Parent Guide ID'
  };

  const fieldDescriptions = {
    // POI fields
    ai_settings: 'Controls for AI model selection',
    name: 'POI title/name',
    subtitle: 'Descriptive subtitle for the POI',
    category: 'Category of the POI (e.g., stop, waypoint)',
    tier: 'Access tier (free, premium, platinum)',
    photoUrl: 'Main POI photograph',
    sphericalPhotoUrl: '360-degree spherical photograph',
    coordinates: 'Latitude and Longitude of the POI',
    description: 'A brief summary of the in-depth description',
    inDepthDescription: 'The main, detailed description for audio generation',
    audioUrl: 'Audio narration generation and management',
    estimatedDuration: 'Estimated time to spend at the POI',
    openingHours: 'Opening hours information',
    keywords: 'Searchable keywords and topics',
    historicalContext: 'Supplementary historical information',
    culturalSignificance: 'Supplementary cultural information',
    hiddenGems: 'Supplementary "hidden gems" or tips',
    nextStop: 'Navigation details to the next stop',
  
    // Metadata fields
    isIntroductoryStop: 'Sofia introduction setting for the itinerary',
    ai_generated_personalization: 'AI personalization tab presence',
    thumbnailUrl: 'Main itinerary thumbnail image',
    theme: 'Itinerary theme/style',
    itinerary_name: 'Name/title of the itinerary',
    country: 'Country where the itinerary takes place',
    city: 'City where the itinerary takes place',
    itinerary_duration: 'Total itinerary duration in days',
    itinerary_difficulty: 'Itinerary difficulty level',
    itinerary_rating: 'Itinerary star rating',
    guide_type: 'Guide type (indoor/outdoor/hybrid/bus tour)',
    itinerary_categories: 'Itinerary categories for classification',
    itinerary_tags: 'Searchable tags for the itinerary',
    itinerary_description: 'Detailed description of the itinerary',
    itinerary_coordinates: 'Geographical coordinates for the itinerary',
    translated_itinerary: 'Translation status and parent guide settings',
    parent_guide_id: 'Parent guide ID for translated itineraries',
    filename: 'Save filename for the itinerary JSON',

    // System features
    userManagement: 'Ability to manage users and roles',
    permissionConfig: 'Access to this permission management interface',
    tourPlanning: 'Tour generation and planning features',
    translation: 'Translation capabilities for content',
    audioGeneration: 'Batch audio generation features',

    // Export features
    firebaseExport: 'Save itineraries to Firebase Storage',
    sqlExport: 'Generate SQL commands for database insertion',
    urlGeneration: 'Generate and view public URLs for itineraries',
    saveDraft: 'Save work-in-progress itineraries as drafts',
    saveJSON: 'Download complete guide as JSON file locally',

    // System fields
    SaveLoad: 'Create, edit, and save Itineraries data',
    regeneratePointIds: 'Regenerate all point IDs functionality',
    addNewPoi: 'Add new point of interest functionality',

    // RAG Management
    ragManagement: 'Manage RAG documents and manifest files for enhanced content',

    // Action Buttons
    generateSQL: 'Generate SQL commands for database operations',
    runSQL: 'Execute SQL commands directly on the database (admin/superadmin only)',
    export: 'General export functionality for guides and data',
    loadGuide: 'Load guide from JSON file or URL',

    // Navigation Tabs
    aiTools: 'Access to AI-powered tools and features',
    drafts: 'Manage and access draft itineraries',
    metadata: 'Edit guide information and metadata',
    pois: 'Manage points of interest/stops',
    preview: 'Preview the guide as it will appear to users',
    map: 'View and interact with the map of stops',

    // Analytics
    analytics: 'Access to the analytics dashboard',
    analyticsOverview: 'View overview analytics and statistics',
    analyticsDaily: 'View daily analytics reports',
    analyticsQR: 'View QR scan analytics',
    analyticsAudio: 'View audio playback analytics',
    analyticsGeo: 'View geographic statistics',
    analyticsSessions: 'View user session analytics'
  };

  return (
    <FeatureGuard featureName="permissionConfig" requiredAccess="full">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2>🛡️ Permission Manager</h2>
        <p>Manage permissions and guide access for different user roles.</p>

        {/* Tab Navigation */}
        <div style={{ marginTop: '20px', borderBottom: '2px solid #e9ecef' }}>
          <div style={{ display: 'flex', gap: '0' }}>
            <button
              onClick={() => setActiveTab('field-permissions')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'field-permissions' ? '#007bff' : 'transparent',
                color: activeTab === 'field-permissions' ? 'white' : '#007bff',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
            >
              Field Permissions
            </button>
            <button
              onClick={() => setActiveTab('guide-access')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'guide-access' ? '#007bff' : 'transparent',
                color: activeTab === 'guide-access' ? 'white' : '#007bff',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
            >
              Guide Access
            </button>
          </div>
        </div>
        
        {message && (
          <div style={{ 
            padding: '10px', 
            marginTop: '10px',
            backgroundColor: message.includes('✅') ? '#d4edda' : '#f8d7da',
            border: `1px solid ${message.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`,
            color: message.includes('✅') ? '#155724' : '#721c24',
            borderRadius: '4px'
          }}>
            {message}
          </div>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'field-permissions' && (
        <>
          {/* Access Level Legend */}
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h3>Access Level Guide:</h3>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
          {Object.entries(FIELD_ACCESS_LEVELS).map(([key, value]) => {
            const colors = getAccessColor(value);
            return (
              <div key={value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  padding: '4px 12px', 
                  backgroundColor: colors.bg, 
                  color: colors.color,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}>
                  {value.replace('_', ' ').toUpperCase()}
                </div>
                <span style={{ fontSize: '14px' }}>
                  {value === 'hidden' && 'Field not visible'}
                  {value === 'view_only' && 'Can see but not edit'}
                  {value === 'edit' && 'Can view and modify'}
                  {value === 'full' && 'Complete control + actions'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Permission Tables by Group */}
      {Object.entries(fieldGroups).map(([parentGroup, subGroups]) => (
        <div key={parentGroup} style={{ marginBottom: '30px' }}>
          <div
            style={{
              padding: '15px',
              backgroundColor: '#e9ecef',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}
            onClick={() => toggleParentGroup(parentGroup)}
          >
            <h2 style={{ margin: 0 }}>{parentGroup}</h2>
            <span style={{ fontSize: '18px' }}>
              {expandedGroups[parentGroup].expanded ? '▼' : '▶'}
            </span>
          </div>
          
          {expandedGroups[parentGroup].expanded && (
            Object.entries(subGroups).map(([groupName, fields]) => (
              <div key={groupName} style={{ marginBottom: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden', marginLeft: '20px' }}>
                <div
                  style={{
                    padding: '15px',
                    backgroundColor: '#e3f2fd',
                    borderBottom: expandedGroups[parentGroup].subGroups[groupName] ? '1px solid #bbdefb' : 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onClick={() => toggleSubGroup(parentGroup, groupName)}
                >
                  <h3 style={{ margin: 0, color: '#1976d2' }}>{groupName}</h3>
                  <span style={{ fontSize: '18px', color: '#1976d2' }}>
                    {expandedGroups[parentGroup].subGroups[groupName] ? '▼' : '▶'}
                  </span>
                </div>
                
                {expandedGroups[parentGroup].subGroups[groupName] && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6', minWidth: '200px' }}>Field</th>
                          {roles.map(role => (
                            <th key={role} style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6', minWidth: '150px' }}>
                              <div style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{role}</div>
                              <div style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                                {role === 'viewer' && 'Basic Access'}
                                {role === 'editor' && 'Content Creator'}
                                {role === 'admin' && 'Full Manager'}
                                {role === 'superadmin' && 'System Admin'}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map(fieldName => (
                          <tr key={fieldName} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '12px', fontWeight: 'medium' }}>
                              <div>
                                {fieldDisplayNames[fieldName] ||
                                 fieldName.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^./, str => str.toUpperCase())}
                              </div>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {fieldDescriptions[fieldName] || ''}
                              </div>
                            </td>
                            {roles.map(role => {
                              const currentAccess = permissions[fieldName]?.[role] || 'hidden';
                              const colors = getAccessColor(currentAccess);
                              const key = `${fieldName}-${role}`;
                              const isSaving = saving[key];
                              
                              return (
                                <td key={role} style={{ padding: '12px', textAlign: 'center' }}>
                                  <select
                                    value={currentAccess}
                                    onChange={(e) => handlePermissionChange(fieldName, role, e.target.value)}
                                    disabled={role === 'superadmin' || isSaving}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '4px',
                                      border: `1px solid ${colors.border}`,
                                      backgroundColor: colors.bg,
                                      color: colors.color,
                                      fontSize: '12px',
                                      fontWeight: 'bold',
                                      cursor: role === 'superadmin' ? 'not-allowed' : 'pointer',
                                      opacity: isSaving ? 0.6 : 1
                                    }}
                                  >
                                    {Object.values(FIELD_ACCESS_LEVELS).map(level => (
                                      <option key={level} value={level}>
                                        {level.replace('_', ' ').toUpperCase()}
                                      </option>
                                    ))}
                                  </select>
                                  {role === 'superadmin' && (
                                    <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                                      Always FULL
                                    </div>
                                  )}
                                  {isSaving && (
                                    <div style={{ fontSize: '10px', color: '#007bff', marginTop: '2px' }}>
                                      Saving...
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ))}
        </>
      )}

      {activeTab === 'guide-access' && (
        <div style={{ marginTop: '20px' }}>
          <GuideAccessManager />
        </div>
      )}

      </div>
    </FeatureGuard>
  );
};

export default PermissionManager;
